import React, { useEffect, useState } from 'react';
import { Form, FormInstance, SelectInput } from 'blueprint-form';
import {
  faPaperPlane,
  faShare,
  faTimes
} from '@fortawesome/pro-solid-svg-icons';
import Icon from '../../atoms/icon';
import Button from '../../molecules/button';
import Dialog from '../dialog';
import Pagination from '../pagination';
import { CardBlock, CardFrame } from '../card';
import Modal from '../modal';
import Toast from '../toast';
import PushMessageFilter from './filter';
import PMTemplateCard from './card';
import { GetFullName, IPatient } from '../../../libs/patient';
import {
  GetPMTemplatesParams,
  IPMTemplate,
  SendPMResponse,
  useGetPMTemplates
} from '../../../libs/pm';
import { FormatPhone } from '../../../libs/format';
import { useApps } from '../../../contexts/apps';
import { useGetWaiversForPatient } from '../../../libs/waiver';
import { AppKey } from '../../../libs/apps';
import { PostJSON } from '../../../libs/request';
import { useTerms } from '../../../contexts/terms';
import DismissibleCallout from '../callout/dismissible-callout';
import { IWaiver } from '../../../libs/waiver';
import { usePinnedTemplates } from '../../../libs/hooks/usePinnedTemplates';

interface PushMessageFormData {
  app: string;
}

interface PushMessageFormProps {
  form: FormInstance<PushMessageFormData>;
  sortedWaivers: IWaiver[];
  onSubmit: (data: PushMessageFormData) => void;
}

const PushMessageForm = ({
  form,
  sortedWaivers,
  onSubmit
}: PushMessageFormProps): JSX.Element => {
  const { apps: appConfigs } = useApps().data || {};
  const options = sortedWaivers
    .filter((waiver) => appConfigs?.[waiver.name])
    .map((waiver) => ({
      label: appConfigs![waiver.name].name,
      value: waiver.name
    }));
  return (
    <Form form={form} onSubmit={onSubmit}>
      <SelectInput
        label="From This Program Number"
        name="app"
        options={options}
        fill
      />
    </Form>
  );
};

interface PushMessageProps {
  isOpen: boolean;
  close: () => void;
  payload?: {
    patient: IPatient;
    templateType?: GetPMTemplatesParams['templateType'];
    useTemplate?: (data: string) => void;
  };
}

const TEMPLATE_PAGE_LIMIT = 5;

const PushMessage = ({
  isOpen,
  close,
  payload
}: PushMessageProps): JSX.Element | null => {
  if (!payload) {
    return null;
  }
  const { patient, useTemplate } = payload;
  const { apps } = useApps().data || {};
  const isChat = payload.templateType === 'chat';
  const { terms } = useTerms();
  // Flag to prevent user action from accidentally closing Dialog as it transitions into view
  const [isInTransition, setIsInTransition] = useState(false);
  const [page, setPage] = useState(1);
  const [tag, setTag] = useState('');
  const [appFilter, setAppFilter] = useState('');
  const [query, setQuery] = useState('');
  const [currentTemplate, setCurrentTemplate] = useState<IPMTemplate | null>(
    null
  );
  const [isSending, setIsSending] = useState(false);
  const { pinnedIds, isPinned, togglePin, isUpdating } = usePinnedTemplates();
  const hasActiveFilters =
    tag?.length > 0 || appFilter?.length > 0 || query?.length > 0;
  const { templates, pinnedTemplates, total } = useGetPMTemplates({
    tag,
    app: appFilter,
    body: query,
    page: page - 1,
    templateType: payload.templateType,
    limit: TEMPLATE_PAGE_LIMIT,
    pinnedIds: hasActiveFilters ? [] : pinnedIds
  });

  const handleClose = () => {
    close();
  };

  const filter = (tag: string, appFilter: string, query: string) => {
    setTag(tag);
    setAppFilter(appFilter);
    setQuery(query);
    if (tag?.length > 0 || appFilter?.length > 0 || query?.length > 0) {
      setPage(1);
      setCurrentTemplate(null);
    }
  };

  // Selection logic
  const { waivers: _waivers } = useGetWaiversForPatient(patient.id);
  const waivers = _waivers.filter((waiver) => waiver.isConfirmed);
  // Sort waivers to prioritize active ones
  const sortedWaivers = waivers.sort((a, b) => {
    if (a.isActive && !b.isActive) return -1;
    if (!a.isActive && b.isActive) return 1;
    return 0;
  });
  const selectableWaivers = sortedWaivers.filter((waiver) => {
    const app = apps?.[waiver.name as AppKey];
    return !!app && app.twilio?.trim().length > 0;
  });
  const appKeys = selectableWaivers.map((waiver) => waiver.name as AppKey);
  const form = new FormInstance<PushMessageFormData>({
    app: ''
  });
  useEffect(() => {
    if (appKeys.length > 0) {
      // Find the first active program to set as default
      const firstActiveWaiver = selectableWaivers.find(
        (waiver) => waiver.isActive
      );
      const defaultAppKey = firstActiveWaiver
        ? firstActiveWaiver.name
        : appKeys[0];
      form.setData({
        app: defaultAppKey
      });
      return;
    }
    form.setData({
      app: ''
    });
  }, [appKeys.join(',')]);

  // Push logic
  const sendPushMessage = (data: PushMessageFormData) => {
    if (isSending) {
      return;
    }
    if (!currentTemplate) {
      return;
    }
    const selectedApp = data.app ? apps?.[data.app as AppKey] : undefined;
    if (!data.app || !selectedApp) {
      Modal.error({
        title: 'Error Sending Push Message',
        content:
          'The selected program is invalid. Please refresh and try again.',
        portalClassName: 'z-100'
      });
      return;
    }
    if (!selectedApp.twilio?.trim()) {
      Modal.error({
        title: 'Error Sending Push Message',
        content:
          'No valid program number is available for the selected program. Please refresh and try again.',
        portalClassName: 'z-100'
      });
      return;
    }
    setIsSending(true);
    PostJSON<SendPMResponse>({
      url: '/pm/send/',
      params: {
        app: data.app,
        templateId: currentTemplate.id,
        patientId: patient.id
      }
    }).then((response) => {
      if (response.status !== 'OK') {
        setIsSending(false);
        Modal.error({
          title: 'Error Sending Push Message',
          content: response.message,
          portalClassName: 'z-100'
        });
        return;
      }
      Toast({
        message: `Push message is sent to ${patient.firstName}!`,
        intent: 'success'
      });
      handleClose();
    });
  };

  // Use Chat Template logic
  const useTemplateForChat = () => {
    if (!currentTemplate) {
      return;
    }

    let text = currentTemplate.body.join('\n');

    // Hash for auto-populating patient variables, can add more in future
    const regexHash = {
      firstName: /\{{ FIRST_NAME \}}|\{{FIRST_NAME\}}/gi,
      lastName: /\{{ LAST_NAME \}}|\{{LAST_NAME\}}/gi
    };

    for (const patientVariable of Object.keys(regexHash) as Array<
      keyof typeof regexHash
    >) {
      const regex = regexHash[patientVariable];
      if (regex.test(text)) {
        text = text.replace(regex, patient[patientVariable]);
      }
    }

    if (useTemplate) {
      useTemplate(text);
    }
    handleClose();
  };

  const handleClick = () => {
    if (isChat) {
      useTemplateForChat();
    } else {
      form.submit();
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      maxWidth="900px"
      onClose={handleClose}
      onOpening={() => setIsInTransition(true)}
      onOpened={() => setIsInTransition(false)}
      canOutsideClickClose={!isInTransition}
      canEscapeKeyClose={!isInTransition}
      className="pb-0"
      forceScrollY
      portalClassName="z-31"
      title={
        isChat ? (
          <>
            <Icon icon={faShare} className="mr-2" /> Chat Templates
          </>
        ) : (
          <>
            <Icon icon={faShare} className="mr-2" /> Push Message to{' '}
            {GetFullName(patient)} - {FormatPhone(patient.phone)}
          </>
        )
      }
    >
      <DismissibleCallout
        intent="warning"
        message={`If a ${terms.patient.default} has opted out of a program they will not receive push messages from that program number if it is selected below.`}
      />
      <CardFrame>
        {waivers && !isChat ? (
          <CardBlock className="pb-3">
            <PushMessageForm
              form={form}
              sortedWaivers={selectableWaivers}
              onSubmit={sendPushMessage}
            />
          </CardBlock>
        ) : null}
        <CardBlock>
          <div className="mb-6">
            <PushMessageFilter
              filter={filter}
              filterBtnMargin="35px"
              showChatTemplateFilter={false}
            />
          </div>
          <div>
            {!hasActiveFilters &&
              pinnedTemplates &&
              pinnedTemplates.length > 0 && (
                <>
                  <div className="mb-3 mt-1">
                    <b className="text-muted">Pinned</b>
                  </div>
                  {pinnedTemplates.map((template) => (
                    <PMTemplateCard
                      key={`pinned-${template.id}`}
                      template={template}
                      apps={apps}
                      onClick={(template) => setCurrentTemplate(template)}
                      isSelected={
                        !!currentTemplate && template.id === currentTemplate.id
                      }
                      isPinned={true}
                      isPinUpdating={isUpdating}
                      onTogglePin={(template) => togglePin(template.id)}
                    />
                  ))}
                  <div className="mb-3 mt-4">
                    <b className="text-muted">All Templates</b>
                  </div>
                </>
              )}
            {templates &&
              templates.map((template) => (
                <PMTemplateCard
                  key={template.id}
                  template={template}
                  apps={apps}
                  onClick={(template) => setCurrentTemplate(template)}
                  isSelected={
                    !!currentTemplate && template.id === currentTemplate.id
                  }
                  isPinned={isPinned(template.id)}
                  isPinUpdating={isUpdating}
                  onTogglePin={(template) => togglePin(template.id)}
                />
              ))}
            {templates &&
              templates.length === 0 &&
              (!pinnedTemplates || pinnedTemplates.length === 0) && (
                <span>No matching templates.</span>
              )}
          </div>
          <Pagination
            page={page}
            total={total}
            limit={TEMPLATE_PAGE_LIMIT}
            onPageChange={setPage}
          />
        </CardBlock>
        <CardBlock>
          <Button
            intent="primary"
            onClick={handleClick}
            disabled={
              currentTemplate == null || (!isChat && appKeys.length === 0)
            }
            loading={isSending}
          >
            <Icon icon={faPaperPlane} className="mr-2" />{' '}
            {isChat ? 'Use Template' : 'Send Push Message'}
          </Button>
          <Button onClick={handleClose} className="ml-4" disabled={isSending}>
            <Icon icon={faTimes} className="mr-2" /> Cancel
          </Button>
        </CardBlock>
      </CardFrame>
    </Dialog>
  );
};

export default PushMessage;
